// Management Insights widget
// Manager hires, attritions, promotions during the last 12 months
// Total hires, attritions, promotions and % change vs last year for each.
import { Box } from '@factorialco/deprecated-design-system'
import { Widget, WidgetAvatarsListItem } from '@factorialco/f0-react/dist/experimental'
import { ManagementInsightDocument, ManagementInsightQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

const prepareData = (data: ManagementInsightQuery) => {
  const hiredEmployees = data.personalHistory.hiringEmployeeOverviewsConnection.nodes.flatMap(
    (overview) => {
      const hires = overview.hires
      const employees = overview.hiredemployeesConnection.nodes.filter(
        (employee): employee is NonNullable<typeof employee> => employee != null
      )

      return hires
        .map((id, _index) => {
          const employee = employees.find((emp) => emp.id === id)
          if (!employee) return null

          return {
            id: employee.id,
            type: 'person' as const,
            src: buildUrl(employee.access?.avatar) ?? undefined,
            firstName: employee.firstName,
            lastName: employee.lastName,
          }
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    }
  )

  const attritionEmployees =
    data.personalHistory.attritionEmployeeOverviewsConnection.nodes.flatMap((overview) => {
      const attritions = overview.attritions
      const employees = overview.attritionemployeesConnection.nodes.filter(
        (employee): employee is NonNullable<typeof employee> => employee != null
      )

      return attritions
        .map((id, _index) => {
          const employee = employees.find((emp) => emp.id === id)
          if (!employee) return null

          return {
            id: employee.id,
            type: 'person' as const,
            src: buildUrl(employee.access?.avatar) ?? undefined,
            firstName: employee.firstName,
            lastName: employee.lastName,
          }
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    })

  const promotionEmployees = data.contracts.promotionEmployeeOverviewsConnection.nodes.flatMap(
    (overview) => {
      const promotions = overview.promotions
      const employees = overview.promotionemployeesConnection.nodes.filter(
        (employee): employee is NonNullable<typeof employee> => employee != null
      )

      return promotions
        .map((id, _index) => {
          const employee = employees.find((emp) => emp.id === id)
          if (!employee) return null

          return {
            id: employee.id,
            type: 'person' as const,
            src: buildUrl(employee.access?.avatar) ?? undefined,
            firstName: employee.firstName,
            lastName: employee.lastName,
          }
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    }
  )

  return {
    hires: {
      total: {
        total: data.personalHistory.hiringEmployeeOverviewsConnection.nodes[0]?.total ?? 0,
        totalLastYear:
          data.personalHistory.hiringEmployeeOverviewsConnection.nodes[0]?.totalLastYear ?? 0,
      },
      hiredEmployees: hiredEmployees,
    },
    attritions: {
      total: {
        total: data.personalHistory.attritionEmployeeOverviewsConnection.nodes[0]?.total ?? 0,
        totalLastYear:
          data.personalHistory.attritionEmployeeOverviewsConnection.nodes[0]?.totalLastYear ?? 0,
      },
      attritionEmployees: attritionEmployees,
    },
    promotions: {
      total: {
        total: data.contracts.promotionEmployeeOverviewsConnection.nodes[0]?.total ?? 0,
        totalLastYear:
          data.contracts.promotionEmployeeOverviewsConnection.nodes[0]?.totalLastYear ?? 0,
      },
      promotionEmployees: promotionEmployees,
    },
  }
}

interface Props {
  data: ReturnType<typeof prepareData>
  employeeId: string
}

const ManagementWidgetComponent = ({ data }: Props) => {
  const { navigation } = useNavigation()
  const linkUrl = navigation.employees.employeeUpdates.fullPath()

  return (
    <Widget
      header={{
        title: i18n.t('insights.widgets.management.title'),
        subtitle: i18n.t('insights.widgets.management.last_twelve_months'),
        link: {
          url: linkUrl,
          title: i18n.t('insights.widgets.management.see_all'),
        },
      }}
    >
      <Box flexDirection="column" gap="s1">
        <WidgetAvatarsListItem
          id="hires"
          title={i18n.t('insights.widgets.management.hires')}
          subtitle={i18n.t('insights.widgets.management.num_people', {
            count: data.hires.total.total,
          })}
          emoji="🤝"
          avatars={data.hires.hiredEmployees}
        />
        <WidgetAvatarsListItem
          id="promotions"
          title={i18n.t('insights.widgets.management.promotions')}
          subtitle={i18n.t('insights.widgets.management.num_people', {
            count: data.promotions.total.total,
          })}
          emoji="🌿"
          avatars={data.promotions.promotionEmployees}
        />
        <WidgetAvatarsListItem
          id="attritions"
          title={i18n.t('insights.widgets.management.attritions')}
          subtitle={i18n.t('insights.widgets.management.num_people', {
            count: data.attritions.total.total,
          })}
          emoji="🚨"
          avatars={data.attritions.attritionEmployees}
        />
      </Box>
    </Widget>
  )
}

export const buildManagementWidget = () =>
  buildWidget({
    id: 'management-insights',
    document: ManagementInsightDocument,
    prepareData,
    component: ManagementWidgetComponent,
    policies: ['seeEmployeePerformanceTab'], // TODO: what policy should we apply? only managers and admins should see this widget
  })
